import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";

import { resolveRailShellPageTitle } from "~/components/dock/dockShellRoutes";
import { FeatureFlagName } from "~/gql/graphqlBase.generated";
import localizedText, { type TranslateKey } from "~/helpers/localizedText";
import { useAppDispatch, useAppSelector } from "~/hooks/reduxHooks";
import useFeatureFlag from "~/hooks/useFeatureFlag";
import { resolveLocationMapLabel } from "~/layout/useModuleBranding";
import { getFeaturePageMapping, getFlatPageMapping, getPageInfo, getSectionIcon } from "~/locationMaps";
import {
	setPageBreadcrumb,
	setPageDescription,
	setPageIcon,
	setPageIdentifier,
	setPageLabel,
	updatePageTitle
} from "~/store/layoutSlice";

const KITS_PAGE_PATH = "/fulfill/product/kits";

const hidePageWhenDisabled = (
	mapping: { [key: string]: PageMapping },
	path: string,
	isEnabled: boolean
): { [key: string]: PageMapping } => {
	if (isEnabled || !(path in mapping)) return mapping;
	const { [path]: _hiddenPage, ...rest } = mapping;
	return rest;
};

const getBreadcrumb = (path: string, flatPageMapping: { [key: string]: PageMapping }, translate: TranslateKey) => {
	if (path === "/")
		return [
			{
				label: "",
				link: ""
			}
		];
	const parts = path.split("/");
	let partPath = "";
	const breadcrumb = parts.map((part) => {
		if (!part) return null;
		partPath += "/" + part;
		const match = Object.keys(flatPageMapping).find((key) => {
			return new RegExp(key + "$").test(partPath);
		});
		if (!match) return null;
		return {
			label: resolveLocationMapLabel(
				partPath,
				flatPageMapping[match].label,
				flatPageMapping[match].labelKey,
				translate
			),
			link: flatPageMapping[match].nav ? partPath : ""
		};
	});
	return breadcrumb.filter((item) => item !== null);
};

const usePageTitle = (): void => {
	const { t } = useTranslation("common");
	const dispatch = useAppDispatch();
	const location = useLocation();
	const application = useAppSelector((state) => state.layout.application);
	const isWideScreen = useAppSelector((state) => state.layout.isWideScreen);
	const { isEnabled: isKitsEnabled } = useFeatureFlag(FeatureFlagName.FulfillKits);

	const flatPageMapping = useMemo(
		() => hidePageWhenDisabled(getFlatPageMapping(application), KITS_PAGE_PATH, isKitsEnabled),
		[application, isKitsEnabled]
	);
	const featurePageMapping = useMemo(() => getFeaturePageMapping(application), [application]);

	useEffect(() => {
		const path = location.pathname;
		const matchedPage = getPageInfo(path, flatPageMapping);
		const railShellTitle = !matchedPage && isWideScreen ? resolveRailShellPageTitle(path, t) : undefined;
		const pageInfo = matchedPage ?? flatPageMapping[Object.keys(flatPageMapping)[0]];
		dispatch(updatePageTitle(railShellTitle ?? resolveLocationMapLabel(path, pageInfo.title, pageInfo.titleKey, t)));
		dispatch(setPageDescription(localizedText(pageInfo.description || "", pageInfo.descriptionKey, t)));
		dispatch(setPageLabel(railShellTitle ?? resolveLocationMapLabel(path, pageInfo.label, pageInfo.labelKey, t)));
		dispatch(setPageIcon(getSectionIcon(path, featurePageMapping) ?? pageInfo.icon ?? ""));
		const breadcrumb = getBreadcrumb(path, flatPageMapping, t);
		dispatch(setPageBreadcrumb(railShellTitle ? [{ label: railShellTitle, link: "" }] : breadcrumb));
		if (!pageInfo.identifier) {
			dispatch(setPageIdentifier(""));
		}
	}, [featurePageMapping, flatPageMapping, isWideScreen, location.pathname, t]);
};

export default usePageTitle;
